import { useEffect, useState } from 'react'
import { Button, Group, Modal, NumberInput, Slider, Stack, Text } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { useTenantBudgetSave } from '@/hooks/useTenantBudget'
import { formatUSD } from '@/utils/finops'

interface BudgetConfigureModalProps {
  opened: boolean
  onClose: () => void
  tenantId: number
  initialBudget?: number | null
  initialThreshold?: number | null
}

export function BudgetConfigureModal({
  opened,
  onClose,
  tenantId,
  initialBudget,
  initialThreshold,
}: BudgetConfigureModalProps) {
  const [budget, setBudget] = useState<number>(initialBudget ?? 1000)
  const [threshold, setThreshold] = useState<number>(initialThreshold ?? 80)

  useEffect(() => {
    if (opened) {
      setBudget(initialBudget ?? 1000)
      setThreshold(initialThreshold ?? 80)
    }
  }, [opened, initialBudget, initialThreshold])

  // Fonte única: grava em budget_thresholds via /budget/me (useTenantBudgetSave),
  // que invalida tanto ['budget', tenantId] quanto ['finops-summary', tenantId].
  const saveMut = useTenantBudgetSave(tenantId)

  const handleSave = () => {
    if (budget <= 0) {
      notifications.show({
        title: 'Budget inválido',
        message: 'Informe um valor maior que zero.',
        color: 'red',
      })
      return
    }
    saveMut.mutate(
      { threshold_usd: budget, alert_at_pct: threshold },
      {
        onSuccess: () => {
          notifications.show({
            title: 'Budget atualizado',
            message: 'Configuração salva com sucesso.',
            color: 'green',
          })
          onClose()
        },
        onError: (e: unknown) =>
          notifications.show({
            title: 'Erro ao salvar budget',
            message: String(e),
            color: 'red',
          }),
      },
    )
  }

  const alertCap = budget * (threshold / 100)

  return (
    <Modal opened={opened} onClose={onClose} title="Configurar Budget Mensal" size="md">
      <Stack>
        <NumberInput
          label="Budget mensal (USD)"
          description="Limite mensal de custo em dólares."
          value={budget}
          onChange={(v) => setBudget(typeof v === 'number' ? v : 0)}
          min={1}
          prefix="$"
          decimalScale={2}
          thousandSeparator=","
        />

        <Stack gap={4}>
          <Group justify="space-between">
            <Text size="sm" fw={500}>Alertar ao atingir</Text>
            <Text size="sm" c="teal" fw={600}>{threshold}%</Text>
          </Group>
          <Slider
            value={threshold}
            onChange={setThreshold}
            min={50}
            max={100}
            step={1}
            marks={[
              { value: 50, label: '50%' },
              { value: 80, label: '80%' },
              { value: 100, label: '100%' },
            ]}
          />
          <Text size="xs" c="dimmed" mt="sm">
            Você receberá alerta quando o forecast 30 dias ultrapassar{' '}
            <strong>{formatUSD(alertCap)}</strong>.
          </Text>
        </Stack>

        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSave} loading={saveMut.isPending}>Salvar</Button>
        </Group>
      </Stack>
    </Modal>
  )
}
